import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { MessageCircle, Phone, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AdminSettingsTab({ contactSettings, setContactSettings }) {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/settings/contact`, contactSettings);
      setContactSettings(res.data);
      toast.success('Contact settings saved successfully!');
    } catch (err) {
      toast.error('Failed to save settings: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-green-500" />WhatsApp & Contact Settings</CardTitle>
        <CardDescription>Manage the contact information shown on the registration page for activation code purchases.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="font-semibold text-sm flex items-center gap-2"><MessageCircle className="h-4 w-4 text-green-500" />WhatsApp Configuration</h3>
            <div className="space-y-2"><Label htmlFor="wa-number">WhatsApp Number (without + or spaces)</Label><Input id="wa-number" value={contactSettings.whatsapp_number || ''} onChange={(e) => setContactSettings(prev => ({ ...prev, whatsapp_number: e.target.value }))} placeholder="201105695504" data-testid="settings-wa-number" /></div>
            <div className="space-y-2"><Label htmlFor="wa-display">Display Number (shown to users)</Label><Input id="wa-display" value={contactSettings.whatsapp_display || ''} onChange={(e) => setContactSettings(prev => ({ ...prev, whatsapp_display: e.target.value }))} placeholder="+20 110 5695504" data-testid="settings-wa-display" /></div>
            <div className="space-y-2"><Label htmlFor="wa-message">Default WhatsApp Message</Label><Input id="wa-message" value={contactSettings.whatsapp_message || ''} onChange={(e) => setContactSettings(prev => ({ ...prev, whatsapp_message: e.target.value }))} placeholder="Hello! I would like to purchase an activation code." data-testid="settings-wa-message" /></div>
          </div>
          <div className="space-y-4">
            <h3 className="font-semibold text-sm flex items-center gap-2"><Phone className="h-4 w-4 text-blue-500" />Phone Configuration</h3>
            <div className="space-y-2"><Label htmlFor="phone-number">Phone Number (tel: link)</Label><Input id="phone-number" value={contactSettings.phone_number || ''} onChange={(e) => setContactSettings(prev => ({ ...prev, phone_number: e.target.value }))} placeholder="+201105695504" data-testid="settings-phone-number" /></div>
            <div className="space-y-2"><Label htmlFor="phone-display">Display Number (shown to users)</Label><Input id="phone-display" value={contactSettings.phone_display || ''} onChange={(e) => setContactSettings(prev => ({ ...prev, phone_display: e.target.value }))} placeholder="+20 110 5695504" data-testid="settings-phone-display" /></div>
            <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-500/20">
              <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Preview</p>
              <div className="flex gap-2">
                <a href={`https://wa.me/${contactSettings.whatsapp_number}?text=${encodeURIComponent(contactSettings.whatsapp_message || '')}`} target="_blank" rel="noopener noreferrer"><Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"><MessageCircle className="h-3 w-3 mr-1" /> WhatsApp</Button></a>
                <a href={`tel:${contactSettings.phone_number}`}><Button size="sm" variant="outline"><Phone className="h-3 w-3 mr-1" /> Call</Button></a>
              </div>
              <p className="text-xs text-muted-foreground mt-2">{contactSettings.phone_display}</p>
            </div>
          </div>
        </div>
        <Button className="w-full sm:w-auto bg-teal-500 hover:bg-teal-600" disabled={saving} onClick={handleSave} data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Save Settings
        </Button>
      </CardContent>
    </Card>
  );
}
